/**
 * Adding documents to a repository — the panel that decides how they arrive.
 *
 * What this replaces was a bare file input: picking a file filed it
 * immediately, against whichever kind of document happened to be first, with no
 * metadata and no preview. Every consequence of that was discovered afterwards
 * — the wrong type, an empty metadata form on a document that now has a
 * retention clock, a file the type never accepted, a scan verdict nobody saw.
 *
 * So the whole decision is made BEFORE anything is filed:
 *
 *  * **Write** — pick from the kinds of document that accept authoring, fill in
 *    the metadata, and open the editor. Which types appear, and whether the
 *    button is offered at all, is decided by the TYPE: a type that is uploaded
 *    only is not something you can start writing.
 *  * **Upload** — drag in as many files as you like, see what each one
 *    actually is, see the malware verdict, preview it, then set the document
 *    type and metadata — for one file or for a whole selection at once.
 *
 * Two rules the panel holds to:
 *
 * 1. **It never claims a check happened that did not.** The scan badge shows
 *    `unavailable` where there is no scanner (a downloaded application has
 *    none), and says so loudly when the repository REQUIRES a scan it has not
 *    got — because then every document will be blocked at ingest, and a panel
 *    is a much better place to learn that than a stuck queue.
 * 2. **Its verdicts are advisory.** The server re-runs every check on upload
 *    and the ingest stage scans again. Nothing here is a control; it is what
 *    stops a person wasting their time.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  formatBytes,
  type DmsClient, type DmsDocType, type DmsField, type DmsFileRules,
  type DmsFolder, type DmsPreflight, type DmsUploadPolicy, type DmsUser,
} from './contracts'
import {
  defaultIsServerSide, generatedOnFiling, hasFieldDefault,
  isProtectedField, MetadataFieldInput,
  resolveFieldDefault,
} from './MetadataFieldInput'
import { Icon } from './icons'

export interface AddDocumentsModalProps {
  client: DmsClient
  types: DmsDocType[]
  folders: DmsFolder[]
  /** The folder the reader is currently browsing — where files land. */
  folderId: string
  onClose: () => void
  /** Called after anything was filed, so the list behind refreshes. */
  onDone: () => void | Promise<void>
  /** Opens the authoring editor. Absent ⇒ the Write path is not offered. */
  onAuthor?: (mode: {
    kind: 'new'
    doctypeId: string
    folderId?: string
    /** The name the author gave it here — see `AuthoringMode`. */
    title?: string
    metadata?: Record<string, unknown>
  }) => void
  /** Re-read the type list after one was defined here. Absent ⇒ the panel
   *  still creates types, it just cannot refresh what is behind it. */
  onTypesChanged?: () => void | Promise<void>
  /** The signed-in person's key, so a field defaulted to `{{user}}` arrives
   *  filled in with them rather than empty. Absent ⇒ the server fills it on
   *  save, as it always did. */
  userKey?: string
}

type Path = 'write' | 'upload'

interface Staged {
  id: string
  file: File
  doctypeId: string
  title: string
  values: Record<string, string>
  check?: DmsPreflight
  checking: boolean
  status: 'staged' | 'uploading' | 'done' | 'failed'
  error?: string
}

let counter = 0
const nextId = () => `f${++counter}`

export function AddDocumentsModal({
  client, types, folders, folderId, onClose, onDone, onAuthor, onTypesChanged,
  userKey,
}: AddDocumentsModalProps) {
  const authorable = useMemo(
    () => types.filter((t) => (t.authoring?.mode || 'off') !== 'off'), [types])
  const uploadable = useMemo(
    () => types.filter((t) => (t.authoring?.mode || 'off') !== 'required'),
    [types])

  // The Write tab is offered whenever the host can open the editor at all —
  // NOT only when a type already allows writing.
  //
  // Gating the tab on `authorable.length` meant a repository whose types were
  // all left at the default `authoring.mode: "off"` — which is every new
  // repository, deliberately — simply had no way to draft a document and
  // nothing anywhere saying why or how to get one. An absent option teaches
  // nobody. The panel now shows the path and explains what has to be true for
  // it to work, with the fix one click away.
  const [users, setUsers] = useState<DmsUser[] | undefined>(undefined)
  useEffect(() => {
    if (!client.listUsers) return
    let live = true
    void (async () => {
      try {
        const list = await client.listUsers!()
        if (live) setUsers(list)
      } catch {
        // A USER field falls back to a text box; the panel still works.
      }
    })()
    return () => { live = false }
  }, [client])

  const canWrite = !!onAuthor
  const [path, setPath] = useState<Path>(
    uploadable.length ? 'upload' : (canWrite ? 'write' : 'upload'))

  if (typeof document === 'undefined') return null

  // Portalled to <body>, like every other modal in this package.
  //
  // `position: fixed` is resolved against the nearest ancestor with a
  // `transform`, `filter` or `contain` — not against the viewport — so mounted
  // inline inside the run shell this overlay was laid out against whichever
  // wrapper happened to have one. It came out narrower than the window, offset
  // to one side, and painted UNDER the application header, which clipped its
  // own title bar. Nothing about the CSS was wrong; the containing block was.
  return createPortal(
    <div className="twa-add" role="dialog" aria-label="Add documents"
      aria-modal="true">
      <div className="twa-add-card">
        <header className="twa-add-head">
          <h2>Add documents</h2>
          {/* One control, two ways in — see the note on `.twa-add-paths`. */}
          <div className="twa-add-paths" role="tablist"
            aria-label="How the documents arrive">
            {canWrite && (
              <button
                type="button"
                role="tab"
                aria-selected={path === 'write'}
                className={`twa-add-path${path === 'write' ? ' is-active' : ''}`}
                onClick={() => setPath('write')}
              >
                <Icon name="pencil" />
                Write a document
              </button>
            )}
            <button
              type="button"
              role="tab"
              aria-selected={path === 'upload'}
              className={`twa-add-path${path === 'upload' ? ' is-active' : ''}`}
              onClick={() => setPath('upload')}
              disabled={!uploadable.length}
              title={uploadable.length ? undefined
                : 'Every kind of document in this repository is written in the editor'}
            >
              <Icon name="upload" />
              Upload files
            </button>
          </div>
          <button type="button" className="twa-add-close" onClick={onClose}
            aria-label="Close">
            <Icon name="close" size={18} width={1.6} />
          </button>
        </header>

        {path === 'write' ? (
          <WritePanel
            client={client}
            folders={folders}
            // EVERY type, not only the ones already opted in. A list that
            // silently omitted the repository's own types read as "this
            // repository has nothing", when the truth is "these exist and none
            // of them is set up to be written here yet" — which is a different
            // problem with a different fix, and one the panel can now offer.
            types={types}
            folderId={folderId}
            users={users}
            userKey={userKey}
            onAuthor={(input) => { onAuthor?.(input); onClose() }}
            onTypeCreated={onTypesChanged}
          />
        ) : (
          <UploadPanel
            client={client}
            types={uploadable}
            allTypes={types}
            folders={folders}
            folderId={folderId}
            onDone={onDone}
            onClose={onClose}
            users={users}
            userKey={userKey}
            onTypesChanged={onTypesChanged}
          />
        )}
      </div>
    </div>,
    document.body,
  )
}

/**
 * Defining a kind of document without leaving the panel.
 *
 * A repository with no suitable type is a dead end at exactly the moment
 * somebody is trying to file something, and "go to the designer, find the
 * Types tab, come back" loses the file they were holding. The type is stamped
 * `runtime`, so a later design seed leaves it exactly as it was left here.
 *
 * Deliberately the SHORT form — key, name, whether it is written or uploaded,
 * and the two clocks. Metadata fields, file rules, AI mode, approval chains
 * and rights are the designer's job; offering a half-form of them here would
 * produce types nobody can see they configured badly.
 */
function NewTypeForm({ client, writable, onCancel, onCreated }: {
  client: DmsClient
  /** Seed authoring on, because the caller reached this from the Write path. */
  writable?: boolean
  onCancel: () => void
  onCreated: (id: string) => void | Promise<void>
}) {
  const [title, setTitle] = useState('')
  const [key, setKey] = useState('')
  const [authoring, setAuthoring] = useState(writable ? 'optional' : 'off')
  const [retention, setRetention] = useState('')
  const [review, setReview] = useState('')
  const [fields, setFields] = useState<DraftField[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [touchedKey, setTouchedKey] = useState(false)

  // The key follows the name until somebody edits it — a key is a required
  // identifier most people should never have to think about, but it is not
  // ours to silently change once they have.
  const effectiveKey = touchedKey ? key : slugKey(title)

  const create = async () => {
    if (!client.createType) return
    if (!title.trim() || !effectiveKey.trim()) {
      setError('A kind of document needs a name.')
      return
    }
    setBusy(true)
    setError('')
    try {
      const result = await client.createType({
        key: effectiveKey,
        title: title.trim(),
        authoring,
        retentionYears: Number(retention) || undefined,
        reviewMonths: Number(review) || undefined,
        fields: fields
          .filter((f) => f.title.trim())
          .map((f) => ({
            key: f.key || slugKey(f.title),
            title: f.title.trim(),
            data_type: f.data_type,
            mandatory: f.mandatory,
            default: f.default.trim() || undefined,
          })),
      })
      await onCreated(result.id)
    } catch (createError) {
      setError(createError instanceof Error ? createError.message
        : 'The kind of document could not be created.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <h3 className="twa-add-h3">New kind of document</h3>
      <p className="twa-add-help">
        Everything else about it — the details it asks for, what files it accepts, who
        may see it, and who approves it — is set in the designer's Types and
        Rights tabs.
      </p>
      <div className="twa-add-fields">
        <label className="twa-add-field">
          <span>Name<b className="twa-add-req"> *</b></span>
          <input value={title} placeholder="e.g. Contract, Policy, Invoice"
            onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="twa-add-field">
          <span>Key</span>
          <input value={effectiveKey} placeholder="e.g. CONTRACT"
            onChange={(e) => { setTouchedKey(true); setKey(slugKey(e.target.value)) }} />
        </label>
        <label className="twa-add-field">
          <span>How documents are created</span>
          <select value={authoring} onChange={(e) => setAuthoring(e.target.value)}>
            <option value="off">Uploaded only</option>
            <option value="optional">Written here or uploaded</option>
            <option value="required">Written here only</option>
          </select>
        </label>
        <label className="twa-add-field">
          <span>Keep for (years)</span>
          <input type="number" min={0} value={retention} placeholder="7"
            onChange={(e) => setRetention(e.target.value)} />
        </label>
        <label className="twa-add-field">
          <span>Re-review every (months)</span>
          <input type="number" min={0} value={review} placeholder="12"
            onChange={(e) => setReview(e.target.value)} />
        </label>
      </div>
      <h4 className="twa-add-h3" style={{ marginTop: 14 }}>Metadata fields</h4>
      <p className="twa-add-help">
        What every document of this type carries. A field with a DEFAULT is
        filled in automatically when the person filing does not supply it —
        <code>{'{{today}}'}</code>, <code>{'{{today+12m}}'}</code>,
        {' '}<code>{'{{user}}'}</code>, or a constant. A <b>Person</b> field
        stores who, and shows their name.
      </p>
      {fields.length === 0 ? (
        <p className="twa-add-help">
          None yet — a kind with no fields asks for nothing, so nothing can
          be filled in when a document is filed against it.
        </p>
      ) : null}
      {fields.map((field, index) => (
        <div key={index} className="twa-add-typefield">
          <input
            value={field.title}
            placeholder="Label, e.g. Owner"
            onChange={(e) => setFields((current) => current.map((f, i) => (
              i === index
                ? { ...f, title: e.target.value,
                    key: f.touchedKey ? f.key : slugKey(e.target.value) }
                : f)))}
          />
          <input
            value={field.key}
            placeholder="KEY"
            onChange={(e) => setFields((current) => current.map((f, i) => (
              i === index
                ? { ...f, key: slugKey(e.target.value), touchedKey: true } : f)))}
          />
          <select
            value={field.data_type}
            onChange={(e) => setFields((current) => current.map((f, i) => (
              i === index ? { ...f, data_type: e.target.value } : f)))}
          >
            <option value="VARCHAR">Text</option>
            <option value="DATE">Date</option>
            <option value="NUMERIC">Number</option>
            <option value="BOOLEAN">Yes / No</option>
            <option value="USER">Person</option>
          </select>
          <input
            value={field.default}
            placeholder="Default"
            onChange={(e) => setFields((current) => current.map((f, i) => (
              i === index ? { ...f, default: e.target.value } : f)))}
          />
          <label className="twa-add-typefield-req">
            <input
              type="checkbox"
              checked={field.mandatory}
              onChange={(e) => setFields((current) => current.map((f, i) => (
                i === index ? { ...f, mandatory: e.target.checked } : f)))}
            />
            required
          </label>
          <button type="button" className="twa-add-secondary"
            onClick={() => setFields((current) =>
              current.filter((_, i) => i !== index))}
            aria-label={`Remove ${field.title || 'field'}`}>×</button>
        </div>
      ))}
      <button type="button" className="twa-add-secondary"
        onClick={() => setFields((current) => [...current, {
          key: '', title: '', data_type: 'VARCHAR', mandatory: false,
          default: '', touchedKey: false,
        }])}>
        Add field
      </button>

      {error ? <p className="twa-add-warn">{error}</p> : null}
      <div className="twa-add-actions">
        <button type="button" className="twa-add-secondary" onClick={onCancel}
          disabled={busy}>Cancel</button>
        <button type="button" className="twa-add-primary" onClick={() => void create()}
          disabled={busy || !title.trim()}>
          {busy ? 'Creating…' : 'Create type'}
        </button>
      </div>
    </>
  )
}

interface DraftField {
  key: string
  title: string
  data_type: string
  mandatory: boolean
  default: string
  /** Stop mirroring the label into the key once somebody edits the key. */
  touchedKey: boolean
}

/** A key is an identifier: upper snake case, and never empty because the name
 *  happened to be punctuation. */
function slugKey(text: string): string {
  return String(text || '').toUpperCase().replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '').slice(0, 40)
}

// ── writing ────────────────────────────────────────────────────────────────

/**
 * Choosing what to write.
 *
 * The type comes first and decides everything after it: whether authoring is
 * offered at all, which template the document opens in, and which metadata
 * fields there are to fill. A type that is uploaded only never appears here.
 */
/**
 * Step one of writing a document: which kind, what it is called, and the
 * details that ride with it to the first save.
 *
 * The step spine at the top of the right pane exists because this panel is not
 * where the document is filed — the editor is. Without it people filled the
 * form expecting "Write this document" to finish the job, and the editor
 * arrived as a surprise.
 */
function WritePanel({ client, types, folders, folderId, onAuthor, onTypeCreated,
  users, userKey }: {
  client: DmsClient
  types: DmsDocType[]
  folders: DmsFolder[]
  folderId: string
  users?: DmsUser[]
  /** Signed-in person, so a `{{user}}` default lands filled in. */
  userKey?: string
  onAuthor: (input: {
    kind: 'new'; doctypeId: string; folderId?: string; title?: string
    metadata?: Record<string, unknown>
  }) => void
  onTypeCreated?: () => void | Promise<void>
}) {
  const [doctypeId, setDoctypeId] = useState(types.length === 1
    ? String(types[0].id) : '')
  const [values, setValues] = useState<Record<string, string>>({})
  const [writeTitle, setWriteTitle] = useState('')
  const [defining, setDefining] = useState(false)
  const [titleError, setTitleError] = useState('')
  const titleRef = useRef<HTMLInputElement | null>(null)
  const type = types.find((t) => String(t.id) === doctypeId)
  const folderName = folders.find((f) => String(f.id) === folderId)?.name || ''
  const noneWritable = types.length > 0
    && !types.some((t) => (t.authoring?.mode || 'off') !== 'off')

  // Seed the type's declared defaults, exactly as the upload path does — a
  // document written here carries the same metadata obligations as one filed,
  // so it should arrive with the same head start rather than an empty form.
  useEffect(() => {
    const chosen = types.find((t) => String(t.id) === doctypeId)
    if (!chosen) { setValues({}); return }
    const seeded: Record<string, string> = {}
    for (const field of chosen.fields) {
      if (field.secure_json) continue
      const value = resolveFieldDefault(field, {
        doctypeKey: chosen.type_key, userKey })
      if (value) seeded[field.field_key] = value
    }
    setValues(seeded)
  }, [doctypeId, types, userKey])

  // Choosing a kind is what reveals the form, and the form's first step is
  // "Name it" — so put the caret there rather than making the reader cross the
  // panel with the mouse to start typing the one field the primary button is
  // disabled without. Re-runs on every change of kind, including the one the
  // "New kind of document" form selects after it creates a type.
  useEffect(() => {
    if (!doctypeId || defining) return
    setTitleError('')
    titleRef.current?.focus()
  }, [doctypeId, defining])

  return (
    <div className="twa-add-body twa-add-body-write">
      <div className="twa-add-types">
        <div>
          <h3 className="twa-add-h3">Kind of document</h3>
          <p className="twa-add-help" style={{ margin: 0 }}>
            The kind decides the template the document opens in, the details it
            carries, and the controls it is filed under.
          </p>
        </div>

        {noneWritable ? (
          // The state a new repository is actually in, said out loud. Writing
          // is opt-in per type (`authoring.mode`), and a type left at the
          // default accepts uploads only — so the honest answer is not "you
          // cannot write here", it is "no type here is set up for it yet".
          <p className="twa-add-note" style={{ margin: 0 }}>
            None of these kinds is set up to be written here yet — they accept
            uploads only. Turn on authoring for one in the designer&apos;s{' '}
            <b>Types</b> tab, or {client.createType
              ? 'define a new kind below.'
              : 'ask an administrator to.'}
          </p>
        ) : null}

        <div className="twa-add-typelist-scroll">
          <ul className="twa-add-typelist">
            {types.map((t) => {
              const mode = t.authoring?.mode || 'off'
              const required = mode === 'required'
              const writable = mode !== 'off'
              const active = String(t.id) === doctypeId
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    disabled={!writable}
                    aria-pressed={active}
                    className={`twa-add-type${active ? ' is-active' : ''}`}
                    onClick={() => setDoctypeId(String(t.id))}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="twa-add-type-head">
                        <span className="twa-add-type-name">
                          {t.title || t.type_key}
                        </span>
                        {!writable ? (
                          <span className="twa-add-type-chip">Upload only</span>
                        ) : null}
                      </span>
                      <span className="twa-add-type-note" style={{ display: 'block' }}>
                        {!writable
                          ? 'Turn on writing for this kind in the designer’s Types tab.'
                          : required ? 'Written here only'
                            : 'Written here or uploaded'}
                        {writable && t.authoring?.template
                          ? ` · ${t.authoring.template} template` : ''}
                      </span>
                      {writable ? (
                        <span className="twa-add-type-note" style={{ display: 'block' }}>
                          {t.fields.length
                            ? `Asks for ${t.fields.length} detail${t.fields.length === 1 ? '' : 's'}`
                            : 'Asks for no extra details'}
                        </span>
                      ) : null}
                    </span>
                    {active ? (
                      <span className="twa-add-type-tick">
                        <Icon name="check" size={18} width={2} />
                      </span>
                    ) : null}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>

        {client.createType ? (
          <button type="button" className="twa-add-secondary"
            style={{ width: '100%' }}
            onClick={() => setDefining(true)}>
            <Icon name="plus" width={1.6} />
            New kind of document
          </button>
        ) : null}
      </div>

      <div className="twa-add-detail">
        {defining ? (
          <div className="twa-add-detail-body">
            <NewTypeForm
              client={client}
              writable
              onCancel={() => setDefining(false)}
              onCreated={async (id) => {
                setDefining(false)
                await onTypeCreated?.()
                setDoctypeId(id)
              }}
            />
          </div>
        ) : !type ? (
          <div className="twa-add-detail-body">
            <p className="twa-add-empty">
              {types.length
                ? 'Choose a kind of document to start writing.'
                : 'A kind of document has to exist before anything can be '
                  + 'written against it.'}
            </p>
          </div>
        ) : (
          <>
            <div className="twa-add-detail-body">
              <div className="twa-add-steps">
                <span className="twa-add-step is-active">
                  <span className="twa-add-step-num">1</span>
                  Name it and fill in its details
                </span>
                <span className="twa-add-step-line" />
                <span className="twa-add-step">
                  <span className="twa-add-step-num">2</span>
                  Write it in the editor
                </span>
              </div>

              <h3 className="twa-add-h3">Name it<b className="twa-add-req"> *</b></h3>
              <p className="twa-add-help">
                This is the name people see everywhere — in the library, in
                answers that cite it, and in the record of what happened to it.
                You can change it later.
              </p>
              <input
                id="twa-write-title"
                ref={titleRef}
                className={`twa-add-input${titleError ? ' is-invalid' : ''}`}
                value={writeTitle}
                required
                aria-label="What this document is called"
                aria-required="true"
                aria-invalid={titleError ? true : undefined}
                aria-describedby={titleError ? 'twa-write-title-error' : undefined}
                placeholder={`e.g. ${type.title || type.type_key} for the north site`}
                onChange={(event) => {
                  setWriteTitle(event.target.value)
                  if (titleError) setTitleError('')
                }}
              />
              {titleError ? (
                <div className="twa-add-error" id="twa-write-title-error"
                  role="alert">
                  {titleError}
                </div>
              ) : null}

              <div className="twa-add-rule" />

              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10,
                flexWrap: 'wrap', marginBottom: 12 }}>
                <h3 className="twa-add-h3" style={{ margin: 0 }}>
                  Details for this {type.title || type.type_key}
                </h3>
                <span className="twa-add-type-note">
                  Carried into the document when you first save it
                </span>
              </div>

              <MetadataFields
                type={type}
                values={values}
                users={users}
                client={client}
                onChange={(key, value) =>
                  setValues((v) => ({ ...v, [key]: value }))}
              />

              {folderName ? (
                <div className="twa-add-folder">
                  <Icon name="folder" width={1.4} />
                  Filed into <strong>{folderName}</strong>
                </div>
              ) : null}
            </div>

            <div className="twa-add-detail-foot">
              <span className="twa-add-help" style={{ flex: 1, margin: 0 }}>
                The document is filed when you save it in the editor. It becomes
                the released document only when it is approved.
              </span>
              {/* Clickable even when the name is missing. A disabled button
                  states no reason and cannot be asked for one — the reader is
                  left to work out which of the fields on the panel is holding
                  it. Let the click land, and answer it. */}
              <button
                type="button"
                className="twa-add-primary"
                onClick={() => {
                  const title = writeTitle.trim()
                  if (!title) {
                    setTitleError('This document needs a name before it can be '
                      + 'written — it is how everyone will find it.')
                    titleRef.current?.focus()
                    return
                  }
                  setTitleError('')
                  onAuthor({
                    kind: 'new', doctypeId, folderId,
                    title,
                    metadata: cleanValues(values),
                  })
                }}
              >
                Write this document
                <Icon name="arrowRight" width={1.7} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ── uploading ──────────────────────────────────────────────────────────────

function UploadPanel({ client, types, allTypes, folders, folderId, onDone,
  onTypesChanged, users, userKey,
  onClose }: {
  client: DmsClient
  types: DmsDocType[]
  allTypes: DmsDocType[]
  folders: DmsFolder[]
  folderId: string
  onDone: () => void | Promise<void>
  onTypesChanged?: () => void | Promise<void>
  users?: DmsUser[]
  /** Signed-in person, so a `{{user}}` default lands filled in. */
  userKey?: string
  onClose: () => void
}) {
  const [items, setItems] = useState<Staged[]>([])
  const [defining, setDefining] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [focused, setFocused] = useState('')
  const [side, setSide] = useState<'values' | 'preview'>('values')
  const [policy, setPolicy] = useState<DmsUploadPolicy | null>(null)
  const [dragging, setDragging] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const inputRef = useRef<HTMLInputElement | null>(null)

  // Auto-pick ONLY when there is nothing to pick between. Defaulting to
  // `types[0]` meant every file arrived already typed as whichever type
  // happened to be first, with nothing saying a choice had been made — so the
  // type looked like a fixed property of the panel rather than the decision it
  // is, and a file filed against the wrong one carried the wrong retention,
  // review cycle and approval chain without anybody choosing it. With more
  // than one type the field starts empty and `problemsOf` blocks the upload
  // with "no kind of document chosen".
  const defaultType = types.length === 1 ? String(types[0].id) : ''

  useEffect(() => {
    let live = true
    void (async () => {
      if (!client.uploadPolicy) return
      try {
        const result = await client.uploadPolicy()
        if (live) setPolicy(result)
      } catch {
        // The panel works without it; it just cannot say whether a scan ran.
      }
    })()
    return () => { live = false }
  }, [client])

  /** What the TYPE fills in for a file nobody has typed values for yet. */
  const defaultsFor = useCallback((doctypeId: string, file?: File)
  : Record<string, string> => {
    const type = allTypes.find((t) => String(t.id) === doctypeId)
    if (!type || type.metadata_binding?.obj_name) return {}
    const context = {
      doctypeKey: type.type_key,
      filename: file?.name || '',
      title: (file?.name || '').replace(/\.[^.]+$/, ''),
      userKey,
    }
    const out: Record<string, string> = {}
    for (const field of type.fields) {
      if (field.secure_json) continue
      // A protected field is the repository's, not the filer's. Staging its
      // value here would post it back, and the server would discard it — so
      // the panel shows it (read-only) and sends nothing.
      if (isProtectedField(field)) continue
      const value = resolveFieldDefault(field, context)
      if (value) out[field.field_key] = value
    }
    return out
  }, [allTypes, userKey])

  const add = useCallback(async (files: File[]) => {
    if (!files.length) return
    const staged: Staged[] = files.map((file) => ({
      id: nextId(),
      file,
      doctypeId: defaultType,
      title: file.name.replace(/\.[^.]+$/, ''),
      // Pre-filled from the TYPE, not left blank for somebody to retype. The
      // server would apply these anyway; doing it here is what makes them
      // visible and editable before the file is filed.
      values: defaultsFor(defaultType, file),
      checking: !!client.checkUploads,
      status: 'staged',
    }))
    setItems((current) => [...current, ...staged])
    setSelected((current) => {
      const next = new Set(current)
      for (const item of staged) next.add(item.id)
      return next
    })
    setFocused((current) => current || staged[0].id)

    if (!client.checkUploads) return
    try {
      const results = await client.checkUploads({ files, folderId })
      setItems((current) => current.map((item) => {
        const index = staged.findIndex((s) => s.id === item.id)
        if (index < 0) return item
        return { ...item, checking: false, check: results[index] }
      }))
    } catch (checkError) {
      setItems((current) => current.map((item) => (
        staged.some((s) => s.id === item.id)
          ? { ...item, checking: false } : item)))
      setError(checkError instanceof Error
        ? `The files could not be checked before upload: ${checkError.message}`
        : 'The files could not be checked before upload.')
    }
  }, [client, defaultType, defaultsFor, folderId])

  const patch = useCallback((ids: string[], change: Partial<Staged>) => {
    const wanted = new Set(ids)
    setItems((current) => current.map((item) => (
      wanted.has(item.id) ? { ...item, ...change } : item)))
  }, [])

  const remove = useCallback((id: string) => {
    setItems((current) => current.filter((item) => item.id !== id))
    setSelected((current) => {
      const next = new Set(current)
      next.delete(id)
      return next
    })
    setFocused((current) => (current === id ? '' : current))
  }, [])

  const selection = useMemo(
    () => items.filter((item) => selected.has(item.id)), [items, selected])

  /**
   * Every problem with one staged file: what the server said when it looked at
   * the bytes, plus what the type the user has since chosen would refuse. The
   * second half is computed here so changing the type re-validates instantly
   * instead of re-sending the file — the server enforces both again on upload.
   */
  const problemsOf = useCallback((item: Staged): string[] => {
    const out = [...(item.check?.problems || [])]
    const type = allTypes.find((t) => String(t.id) === item.doctypeId)
    if (!item.doctypeId) out.push('no kind of document chosen')
    if (!item.title.trim()) out.push('no document name')
    if (type) {
      out.push(...ruleProblems(item.file, item.check, type.file_rules))
      if ((type.authoring?.mode || 'off') === 'required') {
        out.push(`'${type.title || type.type_key}' documents are written in the `
          + 'editor and cannot be uploaded')
      }
    }
    return [...new Set(out)]
  }, [allTypes])

  const missingMandatory = useCallback((item: Staged): string[] => {
    const type = allTypes.find((t) => String(t.id) === item.doctypeId)
    if (!type || type.metadata_binding?.obj_name) return []
    return type.fields
      .filter((f) => truthy(f.mandatory)
        && !isProtectedField(f)
        && !String(item.values[f.field_key] ?? '').trim()
        // A field the type declares a default for is not missing — the server
        // fills it on upload. Warning about `{{user}}`-style defaults the
        // browser cannot resolve would report a gap that will not exist.
        && !hasFieldDefault(f))
      .map((f) => f.title || f.field_key)
  }, [allTypes])

  const blocked = items.filter((item) => problemsOf(item).length > 0)
  const incomplete = items.filter((item) => missingMandatory(item).length > 0)
  const pending = items.filter((item) => item.status !== 'done')

  const upload = useCallback(async () => {
    setBusy(true)
    setError('')
    let failures = 0
    for (const item of items) {
      if (item.status === 'done') continue
      if (problemsOf(item).length) {
        patch([item.id], { status: 'failed',
          error: problemsOf(item).join('; ') })
        failures += 1
        continue
      }
      patch([item.id], { status: 'uploading', error: undefined })
      try {
        const filed = await client.upload({
          file: item.file,
          title: item.title || item.file.name,
          doctypeId: item.doctypeId,
          folderId,
          metadata: cleanValues(item.values),
        })
        // A 'W'/'P' rule did not refuse the file, and there is no acknowledge
        // round here — the panel posts the file and its details together. So
        // it is said on the row, or it is never said at all.
        const said = (filed?.advisories || [])
          .map((a) => String(a.message || '')).filter(Boolean)
        patch([item.id], {
          status: 'done',
          error: said.length ? said.join('; ') : undefined,
        })
      } catch (uploadError) {
        failures += 1
        patch([item.id], {
          status: 'failed',
          error: uploadError instanceof Error ? uploadError.message
            : 'This file could not be filed.',
        })
      }
    }
    setBusy(false)
    await onDone()
    if (!failures) onClose()
    else setError(`${failures} file${failures === 1 ? '' : 's'} could not be `
      + 'filed. The rest were filed; fix these and upload again.')
  }, [items, client, folderId, patch, problemsOf, onDone, onClose])

  const focusedItem = items.find((item) => item.id === focused) || null
  const folderName = folders.find((f) => String(f.id) === folderId)?.name || ''

  return (
    <div className="twa-add-body twa-add-body-upload">
      <div className="twa-add-left">
        <div
          className={`twa-add-drop${dragging ? ' is-over' : ''}`}
          onDragOver={(event) => { event.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault()
            setDragging(false)
            void add([...event.dataTransfer.files])
          }}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') inputRef.current?.click()
          }}
        >
          <strong>Drop files here</strong>
          <span>or click to browse — several at once is fine</span>
          {folderName ? <span>They will be filed in {folderName}</span> : null}
          <input
            ref={inputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(event) => {
              const files = [...(event.target.files || [])]
              event.target.value = ''
              void add(files)
            }}
          />
        </div>

        {policy ? <ScanNotice policy={policy} /> : null}
        {error ? <div className="twa-add-error">{error}</div> : null}

        {items.length > 0 && (
          <div className="twa-add-listhead">
            <label className="twa-add-check">
              <input
                type="checkbox"
                checked={selected.size === items.length && items.length > 0}
                ref={(node) => {
                  if (node) {
                    node.indeterminate = selected.size > 0
                      && selected.size < items.length
                  }
                }}
                onChange={(event) => setSelected(event.target.checked
                  ? new Set(items.map((item) => item.id)) : new Set())}
              />
              {selected.size
                ? `${selected.size} of ${items.length} selected`
                : `${items.length} file${items.length === 1 ? '' : 's'}`}
            </label>
            <button type="button" className="twa-add-link"
              onClick={() => { setItems([]); setSelected(new Set()) }}>
              Clear all
            </button>
          </div>
        )}

        <ul className="twa-add-files">
          {items.map((item) => (
            <FileRow
              key={item.id}
              item={item}
              types={types}
              selected={selected.has(item.id)}
              focused={item.id === focused}
              problems={problemsOf(item)}
              missing={missingMandatory(item)}
              onToggle={() => setSelected((current) => {
                const next = new Set(current)
                if (next.has(item.id)) next.delete(item.id)
                else next.add(item.id)
                return next
              })}
              onFocus={() => setFocused(item.id)}
              onType={(value) => patch([item.id],
                { doctypeId: value, values: defaultsFor(value, item.file) })}
              onTitle={(value) => patch([item.id], { title: value })}
              onRemove={() => remove(item.id)}
            />
          ))}
        </ul>
      </div>

      <div className="twa-add-right">
        <div className="twa-add-sidetabs">
          <button
            type="button"
            className={`twa-add-path${side === 'values' ? ' is-active' : ''}`}
            onClick={() => setSide('values')}
          >Set values{selection.length ? ` (${selection.length})` : ''}</button>
          <button
            type="button"
            className={`twa-add-path${side === 'preview' ? ' is-active' : ''}`}
            onClick={() => setSide('preview')}
          >Preview</button>
          {client.createType && !defining ? (
            <button type="button" className="twa-add-path"
              style={{ marginLeft: 'auto' }}
              onClick={() => setDefining(true)}
            >New type…</button>
          ) : null}
        </div>

        {side === 'preview' ? (
          <Preview item={focusedItem} />
        ) : defining ? (
          <NewTypeForm
            client={client}
            onCancel={() => setDefining(false)}
            onCreated={async (id) => {
              setDefining(false)
              await onTypesChanged?.()
              // Apply it to whatever is selected: somebody who defined a type
              // mid-upload defined it FOR these files.
              const ids = selection.map((item) => item.id)
              if (ids.length) {
                setItems((current) => current.map((item) => (
                  ids.includes(item.id)
                    ? { ...item, doctypeId: id, values: {} } : item)))
              }
            }}
          />
        ) : (
          <BulkValues
            types={types}
            users={users}
            client={client}
            selection={selection}
            onSetType={(doctypeId) => {
              const ids = new Set(selection.map((item) => item.id))
              if (!ids.size) return
              setItems((current) => current.map((item) => {
                if (!ids.has(item.id) || item.doctypeId === doctypeId) return item
                // Values belong to a TYPE. Carrying them across a type change
                // would file a value under a field of the same name on a type
                // that means something different by it — so they are replaced
                // by the new type's own declared defaults.
                return { ...item, doctypeId,
                  values: defaultsFor(doctypeId, item.file) }
              }))
            }}
            onSetValue={(key, value) => {
              const ids = new Set(selection.map((item) => item.id))
              if (!ids.size) return
              setItems((current) => current.map((item) => (
                ids.has(item.id)
                  ? { ...item, values: { ...item.values, [key]: value } }
                  : item)))
            }}
          />
        )}
      </div>

      <footer className="twa-add-foot">
        <div className="twa-add-foot-notes">
          {blocked.length > 0 && (
            <span className="twa-add-warn">
              {blocked.length} file{blocked.length === 1 ? '' : 's'} cannot be
              filed as set up — see the problems listed against them.
            </span>
          )}
          {incomplete.length > 0 && (
            <span className="twa-add-note">
              {incomplete.length} file{incomplete.length === 1 ? '' : 's'} are
              missing details they have to have. They can still be filed, but they will
              not pass the review gate until it is filled in.
            </span>
          )}
        </div>
        <button type="button" className="twa-add-secondary" onClick={onClose}
          disabled={busy}>Cancel</button>
        <button
          type="button"
          className="twa-add-primary"
          disabled={busy || !pending.length || blocked.length === items.length}
          onClick={() => void upload()}
        >
          {busy ? 'Filing…'
            : `File ${pending.length - blocked.length} document${
              pending.length - blocked.length === 1 ? '' : 's'}`}
        </button>
      </footer>
    </div>
  )
}

function ScanNotice({ policy }: { policy: DmsUploadPolicy }) {
  if (policy.scan_configured) {
    return (
      <div className="twa-add-note">
        Files are scanned for malware before they are filed, and again when
        they are processed.
      </div>
    )
  }
  if (policy.scan_required) {
    // This is the one worth shouting about: the repository's own setting will
    // block every one of these documents at ingest, and the alternative to
    // saying so here is a queue nobody understands.
    return (
      <div className="twa-add-error">
        This repository requires a malware scan and no scanner is configured, so
        every document filed here will be blocked when it is processed. Ask an
        administrator to configure a scanner first.
      </div>
    )
  }
  return (
    <div className="twa-add-note">
      No malware scanner is configured, so files are not checked. The audit
      trail records this honestly as “not scanned”.
    </div>
  )
}

function FileRow({ item, types, selected, focused, problems, missing, onToggle,
  onFocus, onType, onTitle, onRemove }: {
  item: Staged
  types: DmsDocType[]
  selected: boolean
  focused: boolean
  problems: string[]
  missing: string[]
  onToggle: () => void
  onFocus: () => void
  onType: (value: string) => void
  onTitle: (value: string) => void
  onRemove: () => void
}) {
  const check = item.check
  return (
    <li className={`twa-add-file${focused ? ' is-focused' : ''}`
      + `${problems.length ? ' has-problem' : ''}`}>
      <input type="checkbox" checked={selected} onChange={onToggle}
        aria-label={`Select ${item.file.name}`} />
      <div className="twa-add-file-main">
        {/* The document's NAME, which is not its file name.
            `sop-0087-v5-FINAL(2).pdf` is what somebody's laptop called the
            bytes; "Information Security Incident Response" is what the
            repository is holding. Seeding from the filename is a convenience,
            not an identity — so it is an editable field, on the row, before
            the document is filed and the name becomes the one everybody
            searches by. */}
        <input
          className="twa-add-file-title"
          value={item.title}
          placeholder="Document name"
          aria-label={`Document name for ${item.file.name}`}
          onFocus={onFocus}
          onChange={(event) => onTitle(event.target.value)}
        />
        <span className="twa-add-file-name" title={item.file.name}>
          {item.file.name}
        </span>
        <span className="twa-add-file-facts">
          {check?.type_label || item.file.type || 'Unknown type'}
          {' · '}{formatBytes(item.check?.size_bytes ?? item.file.size)}
          {check && check.extension_agrees === false
            ? ' · the name does not match the contents' : ''}
        </span>
        {problems.length > 0 && (
          <span className="twa-add-file-problem">{problems.join(' · ')}</span>
        )}
        {!problems.length && missing.length > 0 && (
          <span className="twa-add-file-missing">
            Needs: {missing.join(', ')}
          </span>
        )}
      </div>
      <select
        className="twa-add-file-type"
        value={item.doctypeId}
        onChange={(event) => onType(event.target.value)}
        aria-label={`Kind of document for ${item.file.name}`}
      >
        <option value="">Choose a type…</option>
        {types.map((type) => (
          <option key={type.id} value={type.id}>
            {type.title || type.type_key}
          </option>
        ))}
      </select>
      <ScanBadge item={item} />
      <button type="button" className="twa-add-remove" onClick={onRemove}
        aria-label={`Remove ${item.file.name}`}
        disabled={item.status === 'uploading'}>×</button>
    </li>
  )
}

function ScanBadge({ item }: { item: Staged }) {
  if (item.status === 'done') {
    // Filed, but a non-blocking rule had something to say. The file IS in —
    // saying "Filed" alone would bury the one sentence the designer wrote a
    // 'W' rule to produce.
    if (item.error) {
      return <span className="twa-add-badge is-warn" title={item.error}>
        Filed, with a note
      </span>
    }
    return <span className="twa-add-badge is-clean">Filed</span>
  }
  if (item.status === 'uploading') {
    return <span className="twa-add-badge">Filing…</span>
  }
  if (item.status === 'failed') {
    return <span className="twa-add-badge is-blocked" title={item.error}>
      Failed
    </span>
  }
  if (item.checking) return <span className="twa-add-badge">Checking…</span>
  const status = item.check?.scan?.status || ''
  const detail = item.check?.scan?.detail || ''
  if (status === 'clean') {
    return <span className="twa-add-badge is-clean" title={detail}>Clean</span>
  }
  if (status === 'blocked') {
    return <span className="twa-add-badge is-blocked" title={detail}>Blocked</span>
  }
  if (status === 'error') {
    return <span className="twa-add-badge is-warn" title={detail}>Scan failed</span>
  }
  if (!status) return null
  return <span className="twa-add-badge" title={detail}>Not scanned</span>
}

/**
 * The preview.
 *
 * Rendered from the file in the browser, not from the server — nothing has been
 * filed yet, so there is nothing to fetch. Only the three families a business
 * repository actually receives are rendered; anything else says so plainly
 * rather than showing an empty box.
 */
function Preview({ item }: { item: Staged | null }) {
  const [url, setUrl] = useState('')
  const [text, setText] = useState('')

  useEffect(() => {
    setText('')
    if (!item) { setUrl(''); return undefined }
    const kind = item.check?.kind || ''
    const mime = item.check?.content_type || item.file.type || ''
    if (kind === 'text' || mime.startsWith('text/')) {
      setUrl('')
      let live = true
      void item.file.slice(0, 200_000).text().then((body) => {
        if (live) setText(body)
      }).catch(() => { if (live) setText('') })
      return () => { live = false }
    }
    const objectUrl = URL.createObjectURL(item.file)
    setUrl(objectUrl)
    // Revoked on change and unmount: a modal that stages thirty files would
    // otherwise pin all thirty in memory for the life of the page.
    return () => URL.revokeObjectURL(objectUrl)
  }, [item])

  if (!item) {
    return <p className="twa-add-empty">Choose a file to preview it.</p>
  }
  const mime = item.check?.content_type || item.file.type || ''
  const kind = item.check?.kind || ''

  return (
    <div className="twa-add-preview">
      <div className="twa-add-preview-head">
        <strong>{item.file.name}</strong>
        <span>{item.check?.type_label || mime || 'Unknown type'} ·{' '}
          {formatBytes(item.check?.size_bytes ?? item.file.size)}</span>
      </div>
      <div className="twa-add-preview-body">
        {kind === 'image' || mime.startsWith('image/') ? (
          <img src={url} alt={item.file.name} />
        ) : mime === 'application/pdf' || kind === 'pdf' ? (
          <iframe src={url} title={`Preview of ${item.file.name}`} />
        ) : text ? (
          <pre>{text}</pre>
        ) : (
          <p className="twa-add-empty">
            No preview for this kind of file. It will still be filed, and the
            repository will render it in the secure viewer once it is processed.
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * Setting the type and metadata for a SELECTION.
 *
 * This is what makes filing twenty scanned invoices bearable: select them all,
 * choose the type once, fill the fields that are the same for all of them, and
 * apply. Only the fields that were actually filled in are written, so applying
 * "Owner: Operations" to a selection does not blank everybody's Effective date.
 */
/**
 * Setting the type and metadata for the selected files.
 *
 * **Every edit is written straight through to the files.** It used to be held
 * in this component's own state and pushed up only when "Apply" was clicked —
 * so filling the form and then pressing the panel's primary button, "File N
 * documents", filed the documents with none of it. The metadata was not lost
 * on the server or dropped by the upload: it never left this component. A
 * form whose primary action discards it is a trap, and the Apply button was
 * the only thing standing between the two.
 *
 * What is SHOWN is derived from the selection rather than kept beside it, so
 * the pane can never display something different from what will be filed.
 */
function BulkValues({ types, selection, onSetType, onSetValue, users, client }: {
  types: DmsDocType[]
  selection: Staged[]
  users?: DmsUser[]
  onSetType: (doctypeId: string) => void
  onSetValue: (key: string, value: string) => void
  /** Transport for the reference picker and the on-change rules. */
  client?: DmsClient
}) {
  const shared = useMemo(() => {
    const ids = new Set(selection.map((item) => item.doctypeId))
    return ids.size === 1 ? [...ids][0] : ''
  }, [selection])

  // The value the selection agrees on, or '' where it does not — editing then
  // sets it for all of them, which is what a multi-selection is for.
  const values = useMemo(() => {
    const out: Record<string, string> = {}
    if (!selection.length) return out
    const keys = new Set(selection.flatMap((item) => Object.keys(item.values)))
    for (const key of keys) {
      const distinct = new Set(selection.map((item) => item.values[key] ?? ''))
      out[key] = distinct.size === 1 ? [...distinct][0] : ''
    }
    return out
  }, [selection])

  const mixed = useMemo(() => {
    if (selection.length < 2) return new Set<string>()
    const out = new Set<string>()
    const keys = new Set(selection.flatMap((item) => Object.keys(item.values)))
    for (const key of keys) {
      if (new Set(selection.map((item) => item.values[key] ?? '')).size > 1) {
        out.add(key)
      }
    }
    return out
  }, [selection])

  const doctypeId = shared
  const type = types.find((t) => String(t.id) === doctypeId)

  if (!selection.length) {
    return (
      <p className="twa-add-empty">
        Select one or more files to set their kind of document and details
        together.
      </p>
    )
  }

  return (
    <div className="twa-add-bulk">
      <h3 className="twa-add-h3">
        {selection.length === 1 ? selection[0].file.name
          : `${selection.length} files selected`}
      </h3>

      <label className="twa-add-field">
        <span>Kind of document</span>
        <select value={doctypeId}
          onChange={(event) => onSetType(event.target.value)}>
          <option value="">
            {shared ? 'Choose a type…' : 'Several types — choose one to set all'}
          </option>
          {types.map((t) => (
            <option key={t.id} value={t.id}>{t.title || t.type_key}</option>
          ))}
        </select>
      </label>

      {!type ? (
        <p className="twa-add-help">
          Choose a kind of document to see the details it asks for.
        </p>
      ) : type.metadata_binding?.obj_name ? (
        <p className="twa-add-help">
          This kind keeps its details in the <strong>
            {type.metadata_binding.obj_name}
          </strong> record, with that object's own lookups, validations and
          business logic. File the documents here, then complete each one from
          its own screen — a second form over the same columns would bypass all
          of it.
        </p>
      ) : (
        <>
          <MetadataFields
            type={type}
            values={values}
            users={users}
            client={client}
            onChange={onSetValue}
          />
          <p className="twa-add-help">
            {selection.length === 1
              ? 'Saved as you type — these are filed with the document.'
              : `Saved as you type, for all ${selection.length} selected files.`}
            {mixed.size
              ? ` ${mixed.size} field${mixed.size === 1 ? '' : 's'} differ`
                + ' between them and show blank; typing sets all of them.'
              : ''}
          </p>
        </>
      )}
    </div>
  )
}

// ── metadata form ──────────────────────────────────────────────────────────

/**
 * A kind of document's native metadata fields, rendered from its own definition.
 *
 * Image and file fields are deliberately absent: they carry bytes, and a bulk
 * panel that appeared to accept a picture for twenty documents at once would be
 * lying about what it can do. They are captured on the document's own screen.
 */
/**
 * The metadata form for one document type, on the way IN.
 *
 * It owns the on-change rules rather than leaving them to its two callers,
 * because both drive `onChange` through a functional setState — so a rule that
 * fills three fields is three composed calls and lands intact whether the
 * reader is filing one document or forty. The rule's protect result is held
 * here for the same reason: it is a consequence of the values on screen, and it
 * dies with the panel.
 */
function MetadataFields({ type, values, onChange, users, client }: {
  type: DmsDocType
  values: Record<string, string>
  onChange: (key: string, value: string) => void
  users?: DmsUser[]
  /** Transport for a field's reference picker and its on-change rule. Absent
   *  (or a host serving neither) simply leaves both out. */
  client?: DmsClient
}) {
  const [ruleProtect, setRuleProtect] = useState<Record<string, 0 | 1>>({})
  const [ruleError, setRuleError] = useState('')
  const fields = type.fields.filter(
    (f) => !['IMAGE', 'FILE'].includes(String(f.data_type || '').toUpperCase()))
  const carried = type.fields.length - fields.length

  const edit = (key: string, value: string) => {
    onChange(key, value)
    const field = type.fields.find((f) => f.field_key === key)
    if (!field?.has_item_change || !client?.metadataItemChange) return
    setRuleError('')
    void client.metadataItemChange({
      doctypeId: String(type.id), fieldKey: key,
      values: { ...values, [key]: value },
    }).then((result) => {
      if (result?.error) { setRuleError(result.error); return }
      for (const [target, filled] of Object.entries(result?.updates || {})) {
        // Never echo the trigger back — the reader may still be typing in it.
        if (target === key) continue
        onChange(target, filled == null ? '' : String(filled))
      }
      if (result?.protect && Object.keys(result.protect).length) {
        setRuleProtect((prior) => ({ ...prior, ...result.protect }))
      }
    }).catch((exc) => {
      // Said out loud: the fields the rule would have filled are still empty,
      // and silence reads as "this field does nothing".
      setRuleError(exc instanceof Error ? exc.message
        : 'The rule on this field could not run.')
    })
  }

  if (!fields.length) {
    return (
      <p className="twa-add-help">
        This kind of document asks for no extra details
        {carried ? ', apart from ones that hold a file or picture — those are '
          + 'set on the document itself.' : '.'}
      </p>
    )
  }
  return (
    <>
      <div className="twa-add-fields">
        {fields.map((field) => (
          <FieldInput
            key={field.id || field.field_key}
            field={field}
            value={values[field.field_key] ?? ''}
            users={users}
            client={client}
            doctypeId={String(type.id)}
            formValues={values}
            locked={ruleProtect[field.field_key]}
            onChange={(value) => edit(field.field_key, value)}
          />
        ))}
      </div>
      {ruleError ? <p className="twa-add-warn">{ruleError}</p> : null}
      {carried > 0 && (
        <p className="twa-add-help">
          {carried} field{carried === 1 ? '' : 's'} that hold a file or picture
          are set on the document itself once it is filed.
        </p>
      )}
    </>
  )
}

function FieldInput({ field, value, onChange, users, client, doctypeId,
                     formValues, locked }: {
  field: DmsField
  value: string
  onChange: (value: string) => void
  users?: DmsUser[]
  client?: DmsClient
  doctypeId?: string
  formValues?: Record<string, unknown>
  /** An on-change rule's verdict: 1 locks, 0 unlocks, absent leaves the type's
   *  own setting standing. */
  locked?: 0 | 1
}) {
  const type = String(field.data_type || 'VARCHAR').toUpperCase()
  const secure = !!field.secure_json
  const managed = locked === 1 ? true
    : locked === 0 ? false
      : isProtectedField(field)
  // What genuinely needs the full row: structured values and free text, where
  // a third of the panel would truncate every answer.
  const wide = type === 'JSON' || type === 'TEXT'
  return (
    <label className={wide ? 'twa-add-field twa-add-field-wide' : 'twa-add-field'}>
      <span>
        {field.title || field.field_key}
        {truthy(field.mandatory) ? <b className="twa-add-req"> *</b> : null}
        {secure ? <em className="twa-add-secure"> · encrypted</em> : null}
        {managed ? <em className="twa-add-secure"> · set automatically</em> : null}
      </span>
      {managed ? (
        // Shown, never asked for. Hiding it would leave the person wondering
        // what the type does with a mandatory field they were never given —
        // and a read-only box that says what will be filled answers that.
        <ManagedValue field={field} />
      ) : (
        /* The control itself is shared with the document's own metadata panel,
           so the same field cannot behave differently depending on which
           screen you are standing on. */
        <MetadataFieldInput field={field} value={value} onChange={onChange}
          users={users} client={client} doctypeId={doctypeId}
          formValues={formValues} />
      )}
    </label>
  )
}

/** What a system-filled field is about to be filled with. */
function ManagedValue({ field }: { field: DmsField }) {
  const preview = resolveFieldDefault(field)
  const text = generatedOnFiling(field)
    ? 'Numbered when the document is filed'
    : preview || 'Filled in on upload'
  return (
    <input className="twa-add-managed" type="text" value={text} readOnly
           disabled tabIndex={-1}
           title="This kind of document fills this in — it is not typed here." />
  )
}

// ── helpers ────────────────────────────────────────────────────────────────

function truthy(value: unknown): boolean {
  return value === true || value === 1 || value === '1'
    || String(value).toLowerCase() === 'true'
}

/** Drop empty entries — an empty box means "not set", never "clear it". */
function cleanValues(values: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(values)) {
    if (String(value ?? '').trim() !== '') out[key] = value
  }
  return out
}

/**
 * What a kind of document would refuse about a file.
 *
 * A deliberate client-side echo of the server's `check_file_rules`, so choosing
 * a type re-validates instantly instead of re-sending the bytes. The SERVER is
 * the control — it runs the same checks on upload and refuses there — so a
 * disagreement here costs a late error message, never an unchecked document.
 */
function ruleProblems(file: File, check: DmsPreflight | undefined,
  rules: DmsFileRules | undefined): string[] {
  if (!rules) return []
  const out: string[] = []
  const size = check?.size_bytes ?? file.size
  const mime = (check?.content_type || file.type || '').toLowerCase()
  const name = file.name || ''
  const ext = name.includes('.') ? name.split('.').pop()!.toLowerCase() : ''

  if (rules.extensions?.length && !rules.extensions.includes(ext)) {
    out.push(`this type accepts ${rules.extensions.map((e) => `.${e}`).join(', ')}`)
  }
  if (rules.mime_types?.length && mime && !rules.mime_types.includes(mime)) {
    out.push(`this type does not accept ${check?.type_label || mime}`)
  }
  if (rules.min_size_kb && size < rules.min_size_kb * 1024) {
    out.push(`this type requires at least ${rules.min_size_kb} KB`)
  }
  if (rules.max_size_mb && size > rules.max_size_mb * 1024 * 1024) {
    out.push(`this type allows at most ${rules.max_size_mb} MB`)
  }
  return out
}
